/**
 * `Calendar` — o `calendar` do shadcn (preset `base-nova`, react-day-picker 10), copiado pro repo
 * pelo caminho normal e com três adaptações conscientes:
 *
 * ① **`IconPlaceholder` → Phosphor.** O arquivo do registry usa um componente do site do shadcn
 *    (`@/app/(create)/components/icon-placeholder`) que não existe fora dele. A `iconLibrary` do
 *    nosso `components.json` é phosphor, então os carets entram diretos.
 * ② **Sem `"use client"`.** O design-hub é Vite/SPA (`rsc: false`), a diretiva não faz nada aqui e
 *    todo arquivo de `ui/` está sem ela.
 * ③ **`--cell-size` fica no consumidor, não aqui.** O default do registry é `--spacing(7)` (28px);
 *    o `DateRangePicker` do `@openai/apps-sdk-ui` — a referência de escala que o designer aprovou —
 *    usa 30px, o que dá um mês de exatos 210px e dois meses em 452px. Quem monta o painel declara
 *    a medida; o primitivo não decide a densidade da tela.
 *
 * O resto é o arquivo do registry sem mexer. Customizar o CVA/classNames daqui é o caminho normal
 * (AGENTS.md), não gambiarra — mas quem precisar de célula pintada por preço passa `components` e
 * `modifiers` de fora, sem tocar neste arquivo.
 */
import * as React from 'react';
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import {
  DayPicker,
  getDefaultClassNames,
  type DateRange,
  type DayButton,
  type Locale,
  type WeekNumber,
} from 'react-day-picker';

import { cn } from '../lib/utils';
import { Button, buttonVariants } from './button';

/* VOLL: os quatro `components` abaixo saem do corpo do `Calendar`.
 *
 * O `components` do react-day-picker é consumido como TIPO de componente. Declarado
 * inline no JSX (como o registry faz), cada render do `Calendar` entrega um tipo
 * NOVO, e o React desmonta e remonta a árvore inteira — `Root` leva junto os ~60
 * botões de dia. Enquanto nada re-renderiza o calendário ninguém percebe; num
 * consumidor que re-renderiza durante o gesto (hover de dia, por exemplo) o nó em
 * que o mouse APERTOU deixa de existir antes de soltar, o browser não emite
 * `click`, e a escolha de data não acontece. Não é hipótese: derrubou a jornada de
 * hotel no e2e, e `fireEvent.click` não vê porque dispara só o `click`.
 *
 * Quem precisa ser estável é o VALOR de cada chave — a identidade do objeto que as
 * embrulha não conta pra reconciliação. Por isso os três que não fecham sobre nada
 * são funções de módulo, o `DayButton` (que fecha sobre `locale`) vai num `useMemo`
 * de `[locale]`, e a mesclagem com o `components` do consumidor fica SOLTA: memoizar
 * o objeto mesclado não compraria nada e ainda amarraria a estabilidade do nosso
 * `DayButton` à do `components` que vem de fora — um consumidor que passa o objeto
 * inline (como `flight_planner_v2/painel_de_datas.tsx` faz com `Day`) traria a
 * remontagem de volta justamente por causa do conserto.
 *
 * Markup idêntico ao do registry — muda só onde cada função é criada. */
function CalendarRoot({
  className,
  rootRef,
  ...props
}: React.ComponentProps<'div'> & { rootRef?: React.Ref<HTMLDivElement> }) {
  return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
}

function CalendarChevron({
  className,
  orientation,
  ...props
}: React.ComponentProps<'svg'> & { orientation?: 'left' | 'right' | 'up' | 'down' }) {
  if (orientation === 'left') {
    return <CaretLeftIcon className={cn('cn-rtl-flip size-4', className)} {...props} />;
  }

  if (orientation === 'right') {
    return <CaretRightIcon className={cn('cn-rtl-flip size-4', className)} {...props} />;
  }

  return <CaretDownIcon className={cn('size-4', className)} {...props} />;
}

/* VOLL: `th`, e o `week` fica de fora do DOM.
 *
 * O registry escrevia `<td>` sem tirar o `week` das props. O rdp chama este slot com
 * `week`, `scope="row"` e `role="rowheader"` (`DayPicker.js:308`) e o próprio
 * componente dele é um `<th>`: `td` numa linha cujo cabeçalho de coluna é `th
 * scope="col"` quebra a semântica da tabela, e o objeto `week` espalhado vira
 * atributo desconhecido no DOM (warning do React). Só aparece com `showWeekNumber`. */
function CalendarWeekNumber(props: React.ComponentProps<typeof WeekNumber>) {
  // `week` é extraído só pra NÃO ir pro DOM — é o objeto da semana do rdp, e
  // espalhado no `th` vira atributo desconhecido. Mesmo descarte que o componente
  // do próprio rdp faz.
  const { children, week, ...thProps } = props;
  return (
    <th {...thProps}>
      <div className="flex size-(--cell-size) items-center justify-center text-center">
        {children}
      </div>
    </th>
  );
}

/** O `DayButton` amarrado ao `locale` — fábrica de módulo, memoizada pelo `Calendar`. */
function dayButtonNoLocale(locale: Partial<Locale> | undefined) {
  return (dayProps: React.ComponentProps<typeof DayButton>) => (
    <CalendarDayButton locale={locale} {...dayProps} />
  );
}

/** O dia como número de dias, pra comparar ORDEM sem depender da hora.
 *
 *  O rdp entrega os dias da grade ao MEIO-DIA (`noonDateLib`), mas o `selected` vem do consumidor, que
 *  costuma montar datas à meia-noite (`dataEmDias` no design-hub) — comparar timestamps cru faria o
 *  mesmo dia parecer anterior a si mesmo. `Date.UTC` porque ele ignora fuso e horário de verão, que é
 *  onde aritmética de "dia seguinte" costuma errar por uma hora. */
function emDiasDoCalendario(dia: Date) {
  return Date.UTC(dia.getFullYear(), dia.getMonth(), dia.getDate()) / 86_400_000;
}

/**
 * A prévia PODE existir? É tudo o que não depende do dia sob o cursor: há primeira data e a segunda
 * ainda está em aberto.
 *
 * O teste do `to` é o mesmo `temVolta` de `flight_planner_v2/painel_de_datas.tsx` — o primeiro clique
 * do rdp em `mode="range"` devolve `from === to`, e isso NÃO é intervalo fechado. Com o intervalo
 * fechado de verdade a prévia tem que ficar apagada: ali a próxima interação REINICIA a seleção (ver
 * `proximaSelecao`), então sugerir uma faixa a partir do `from` antigo mentiria sobre o que o clique
 * vai fazer.
 */
function previaPossivel(selecao: DateRange | undefined) {
  if (selecao?.from == null) return false;
  return selecao.to == null || selecao.to.toDateString() === selecao.from.toDateString();
}

/**
 * A **prévia do intervalo no hover** — a faixa que vai da primeira data até o dia sob o cursor.
 *
 * Padrão de mercado (Google Flights, Airbnb) e chamada do designer (2026-08-17): depois de escolher a
 * primeira data, mover o mouse pela grade tem que mostrar a estadia que aquele clique fecharia. Antes
 * disso nada acontecia até o segundo clique.
 *
 * Devolve `undefined` — prévia DESLIGADA — quando falta ponta (sem primeira data, sem hover), quando o
 * intervalo já fechou (ver `previaPossivel`) ou quando o cursor está no próprio `from`: faixa de zero
 * dias não é faixa.
 *
 * **Pinta nas DUAS direções.** Hover ANTES do `from` também acende, porque o clique ali também fecha um
 * intervalo: o react-day-picker inverte as pontas sozinho e `proximaSelecao` aceita o resultado.
 * `paraFrente` existe só pra o arredondamento saber de que lado está a ponta viva.
 *
 * `meio` é PREDICADO e não `DateRange` porque o `Matcher` do rdp trata `DateRange` como intervalo
 * INCLUSIVO nas duas pontas, e aqui as duas pontas são exceção: o `from` já é ink (a faixa começa onde
 * ele termina, sem vão, porque as células são vizinhas) e o dia em hover leva a classe da ponta
 * arredondada. Predicado também dispensa aritmética de "dia seguinte".
 */
export function previaDeRange(
  selecao: DateRange | undefined,
  emHover: Date | undefined,
): { meio: (dia: Date) => boolean; fim: Date; inicio: Date; paraFrente: boolean } | undefined {
  const de = selecao?.from;
  if (de == null || emHover == null || !previaPossivel(selecao)) return undefined;
  const inicio = emDiasDoCalendario(de);
  const alvo = emDiasDoCalendario(emHover);
  if (inicio === alvo) return undefined;
  const menor = Math.min(inicio, alvo);
  const maior = Math.max(inicio, alvo);
  return {
    meio: (dia) => {
      const n = emDiasDoCalendario(dia);
      return n > menor && n < maior;
    },
    fim: emHover,
    /* O `from` da seleção, devolvido porque a EMENDA é dele: enquanto a prévia está acesa, a célula
       dele completa a faixa por trás do canto arredondado — senão o raio abria um recorte claro na
       junção (a seta do designer, 2026-08-18). Ver `previaNoDayPicker`. */
    inicio: de,
    paraFrente: alvo > inicio,
  };
}

/**
 * O tom da prévia: **neutro translúcido, com o mapa de calor OCULTO sob a faixa** (teste pedido pelo
 * designer, 2026-08-18 — segunda rodada).
 *
 * A história em três atos, porque este tom já mudou duas vezes no mesmo dia: ① nasceu `foreground/10`
 * translúcido com o calor visível por baixo — e o calor fazia a faixa mudar de tom célula a célula,
 * lendo como defeito de render; ② virou o ink da seleção real (`bg-primary` + número creme via
 * `data-previa` no botão) — cobria o calor, mas prévia e seleção ficavam indistinguíveis (ver o print
 * da seta na junção do `from`); ③ o designer pediu o meio-termo: volta o translúcido, e o CONFLITO
 * sai pelo outro lado — a célula em prévia esconde o próprio calor.
 *
 * Quem esconde é o CONSUMIDOR: a constante carrega a classe marcadora **`range-preview`** (sem estilo
 * próprio), e a camada de calor do aéreo se apaga sob ela (`[.range-preview_&]:invisible` em
 * `CalorDoDia`, `flight_planner_v2/painel_de_datas.tsx`). O primitivo não conhece — nem pode
 * conhecer — as classes de calor do design hub; ele só anuncia o estado.
 *
 * **Pintada em `after:` no `<td>`, e é o único lugar que serve.** O fundo do próprio `td` fica ABAIXO
 * das camadas de cor que o consumidor pendura como filhos absolutos (`CalorDoDia` em
 * `flight_planner_v2/painel_de_datas.tsx`); e no botão ela morreria no `hover:bg-transparent!`
 * exatamente na célula sob o cursor, que é a ponta viva da faixa. O pseudo-elemento é o último irmão
 * em z auto: pinta por cima do calor e por baixo do botão (z-10), onde está o número.
 *
 * **Faixa contínua, e sem `!` em lugar nenhum.** Pseudo-elemento não herda o `rounded-(--cell-radius)`
 * do `td`, então o meio já nasce reto e as células vizinhas emendam sem fresta; e como `previaDeRange`
 * e `previaDeRangeFim` nunca caem na MESMA célula, nada disputa com nada (o `getClassNamesForModifiers`
 * do rdp só concatena as classes com espaço — não passa por `tailwind-merge`, então classe conflitante
 * ali viraria a mesma loteria de ordem que a nota do `CalendarDayButton` documenta).
 */
const PREVIA_DE_RANGE = 'range-preview after:absolute after:inset-0 after:bg-foreground/10';

/** As duas chaves que a prévia acende, já mescladas com as do consumidor — `modifiers` e
 *  `modifiersClassNames` são API pública do rdp e há story usando as duas
 *  (`galerias/calendar.stories.tsx`, `DatasIndisponiveis`), então sobrescrever seria regressão.
 *
 *  Fora do corpo do `Calendar` porque é a construção que carrega os ternários — e o cap de
 *  complexidade do repo é 15. */
function previaNoDayPicker(
  previa: ReturnType<typeof previaDeRange>,
  modifiers: React.ComponentProps<typeof DayPicker>['modifiers'],
  modifiersClassNames: React.ComponentProps<typeof DayPicker>['modifiersClassNames'],
) {
  if (!previa) return { modifiers, modifiersClassNames };
  const pontaViva = previa.paraFrente
    ? 'after:rounded-r-(--cell-radius)'
    : 'after:rounded-l-(--cell-radius)';
  /* A EMENDA: enquanto a faixa está acesa, a célula do `from` pinta a prévia na MEIA-CÉLULA voltada
     pra faixa, POR TRÁS do botão ink — é ela que preenche o vão claro que o canto arredondado abria
     na junção (a seta do designer, 2026-08-18; a segunda seta, do dia 10, derrubou a primeira
     solução — achatar o raio do `from` — porque o dia escolhido perdia a forma de dia escolhido).
     Mesma técnica do `range_start`/`range_end` do registry, que emendam as pontas do intervalo real
     com um pseudo-elemento de meia-célula; aqui só muda a cor. O raio do botão não é tocado: o ink
     cobre o centro (z-10) e a prévia aparece exatamente onde o raio não pinta.
     **`before:` e NÃO `after:`, e isso foi medido:** o `from` do aéreo vive como `range_start` E
     `range_end` ao mesmo tempo (o dia-duplo do primeiro clique, `minNoites: 0`), e essas duas chaves
     do primitivo JÁ pintam o `::after` do td (`after:bg-primary after:w-4…`) — a emenda no mesmo
     pseudo-elemento virava a loteria de concatenação que a nota do `PREVIA_DE_RANGE` descreve. O
     `::before` do td não tem dono em receita nenhuma, e pinta na mesma camada (atrás dos filhos e
     do botão).
     Sem `range-preview` na emenda de propósito: o calor do `from` já foi apagado pela SELEÇÃO
     (`pintavel` em `celulaDeCalor`), e a classe aqui só arriscaria esconder camada de um dia que não
     está na faixa. Dois modifiers e não um + flag: a classe muda com a direção. */
  const emenda = previa.paraFrente
    ? { chave: 'previaEmendaADireita', classe: 'before:right-0 before:w-1/2' }
    : { chave: 'previaEmendaAEsquerda', classe: 'before:left-0 before:w-1/2' };
  return {
    modifiers: {
      ...modifiers,
      previaDeRange: previa.meio,
      previaDeRangeFim: previa.fim,
      [emenda.chave]: previa.inicio,
    },
    modifiersClassNames: {
      ...modifiersClassNames,
      previaDeRange: PREVIA_DE_RANGE,
      previaDeRangeFim: `${PREVIA_DE_RANGE} ${pontaViva}`,
      [emenda.chave]: `before:absolute before:inset-y-0 before:bg-foreground/10 ${emenda.classe}`,
    },
  };
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  locale,
  formatters,
  components,
  modifiers,
  modifiersClassNames,
  onDayMouseEnter,
  onDayMouseLeave,
  semPreviaDeRange = false,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
  /**
   * Desliga a prévia de intervalo no hover (ver `previaDeRange`). Default `false` — quem tem duas
   * datas de verdade (ida e volta, check-in e check-out) quer a prévia.
   *
   * Existe por causa de UM caso legítimo: o painel do aéreo em **somente ida** roda em `mode="range"`
   * com o `from` preenchido e o `to` eternamente nulo (a grade é a mesma, a volta é descartada na
   * escolha — ver `soIda` em `flight_planner_v2/painel_de_datas.tsx`). Sem o opt-out, aquele painel
   * ficaria com a prévia acesa pra sempre, prometendo uma faixa que a forma não aceita.
   */
  semPreviaDeRange?: boolean;
}) {
  const defaultClassNames = getDefaultClassNames();
  const DefaultDayButton = React.useMemo(() => dayButtonNoLocale(locale), [locale]);

  /* O dia sob o cursor, e ele só é GUARDADO quando alguma prévia pode sair dele. Guardar sempre
     redesenharia a grade inteira a cada célula que o cursor cruza — três meses de células por nada no
     painel do aéreo, que passa boa parte da vida exatamente nesse estado (só ida, ou nada escolhido).
     Redesenhar é seguro (é o que a nota do topo do arquivo garante ao manter o VALOR de cada
     `components` estável entre renders), mas seguro não é grátis. */
  const [emHover, setEmHover] = React.useState<Date>();
  /* `props.selected` só é intervalo em `mode="range"`, e a união do rdp não correlaciona os dois — a
     leitura estreita fica aqui, uma vez, em vez de espalhar narrowing pelo corpo.
     **Só serve pra seleção CONTROLADA**, e é limitação assumida: em modo não controlado o rdp guarda a
     escolha dentro dele e `selected` chega vazio, então não sai prévia (o `Playground` de
     `galerias/calendar.stories.tsx` é o único lugar assim). Ler a seleção interna exigiria um
     `components` novo pra alcançar o `useDayPicker()` — e componente que fecha sobre estado é
     exatamente a remontagem que a nota do topo deste arquivo existe pra evitar. Todo consumidor de
     produto é controlado. */
  const selecao = props.mode === 'range' ? props.selected : undefined;
  const previaLigada = !semPreviaDeRange && previaPossivel(selecao);
  const previa = previaLigada ? previaDeRange(selecao, emHover) : undefined;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-background p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        /* `flex-row` por padrão. O registry traz `flex-col md:flex-row`, e o `md:` é MEDIA QUERY —
           mede o viewport. Aqui o que aperta um calendário nunca é a janela: é a Trip abrindo e
           comendo 432px da coluna da conversa, com o viewport intacto em 1440. É a mesma armadilha
           que o `FareSelectorV2` já documentou.
           Empilhar EXISTE, mas é decisão do consumidor, não do primitivo (pedido do designer: em
           container estreito os meses viram coluna ROLÁVEL, padrão Airbnb/Google Flights mobile —
           ver `CalendarioDoPainel` no design-hub). Quem empilha repõe esta chave inteira via
           `classNames.months` (`flex flex-col gap-6`) e troca junto as props de navegação
           (`hideNavigation`, `numberOfMonths`) — direção de flex sozinha não faz o modo. */
        months: cn('relative flex flex-row gap-4', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'cn-calendar-dropdown-root relative rounded-(--cell-radius)',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('bg-popover absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label'
            ? 'cn-calendar-caption text-sm'
            : 'cn-calendar-caption-label flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:text-muted-foreground [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        month_grid: cn('w-full border-collapse', defaultClassNames.month_grid),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal select-none',
          defaultClassNames.weekday,
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
        week_number: cn(
          'text-muted-foreground text-[0.8rem] select-none',
          defaultClassNames.week_number,
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)',
          props.showWeekNumber
            ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
            : '[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
          defaultClassNames.day,
        ),
        /* VOLL: `--sand-100` no lugar do `muted` (sand-50) do registry nas TRÊS peças do intervalo.
           Motivo medido, não gosto: num calendário onde o fundo da célula já carrega OUTRA informação
           (o mapa de calor de preço), a barra do intervalo é a única coisa desenhada na área
           selecionada — e sand-50 sobre card branco é imperceptível. Subiu dois degraus.
           Token cru e não slot semântico porque nenhum dos slots cai em sand-100: `muted` e
           `secondary` são sand-50, `accent` é sand-75 (testado, ainda apagado) e o próximo é
           `border`, que significa outra coisa. */
        /* **Dia que é começo E fim ao mesmo tempo** (`{from: d, to: d}` — o primeiro clique do rdp
           em modo range, e o bate-e-volta legítimo do aéreo): a célula recebe as DUAS strings
           abaixo, e os dois `after:` colidem — `right-0` + `left-0` sobre-restringem o absolute,
           em LTR o `right` cai, e sobra uma barra QUADRADA de 16px pintada na metade esquerda, por
           cima do raio. A barra existe pra emendar a célula na vizinha do intervalo; dia sozinho
           não tem vizinho pra emendar, então o `:has` a esconde quando o botão carrega os dois
           marcadores. Os raios não conflitam: `rounded-l` + `rounded-r` somam pro raio cheio. */
        range_start: cn(
          'bg-primary after:bg-primary relative isolate z-0 rounded-l-(--cell-radius) after:absolute after:inset-y-0 after:right-0 after:w-4 [&:has([data-range-end=true])]:after:hidden',
          defaultClassNames.range_start,
        ),
        range_middle: cn('bg-primary rounded-none', defaultClassNames.range_middle),
        range_end: cn(
          'bg-primary after:bg-primary relative isolate z-0 rounded-r-(--cell-radius) after:absolute after:inset-y-0 after:left-0 after:w-4',
          defaultClassNames.range_end,
        ),
        today: cn(
          'bg-muted text-foreground rounded-(--cell-radius) data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: CalendarRoot,
        Chevron: CalendarChevron,
        DayButton: DefaultDayButton,
        WeekNumber: CalendarWeekNumber,
        ...components,
      }}
      {...previaNoDayPicker(previa, modifiers, modifiersClassNames)}
      /* **Os handlers do rdp caem no DayButton, não no `<td>`** — verificado no fonte
         (`DayPicker.js`: `onMouseEnter: handleDayMouseEnter(day, modifiers)` vive no
         `components.DayButton`). É o que faz a prévia funcionar mesmo onde o consumidor substitui o
         `components.Day` inteiro: `celulaDeCalor` (aéreo) troca o `<td>` e embrulha ele num
         `TooltipTrigger`, e nada disso fica no caminho do evento — ele nasce no botão, que continua
         sendo o nosso `CalendarDayButton`.
         O do consumidor é CHAMADO, não substituído: `onDayMouseEnter` é API pública do rdp.
         O apagar mora no `mouseleave` de cada dia, que é também o mouseleave da GRADE: sair do
         calendário por qualquer borda passa pelo último dia visitado. Entre duas células vizinhas o
         browser dispara leave e enter no mesmo ciclo e o React agrupa os dois estados, então a faixa
         não pisca na travessia. */
      onDayMouseEnter={(dia, diaModifiers, evento) => {
        if (previaLigada) setEmHover(dia);
        onDayMouseEnter?.(dia, diaModifiers, evento);
      }}
      onDayMouseLeave={(dia, diaModifiers, evento) => {
        if (previaLigada) setEmHover(undefined);
        onDayMouseLeave?.(dia, diaModifiers, evento);
      }}
      {...props}
    />
  );
}

/* VOLL: `bg-clip-border` no botão do dia — o fundo do dia escolhido é a CÉLULA CHEIA.
 *
 * **Isto reverte o `bg-clip-content px-[2px]` que morava aqui** (chamada do designer, 2026-08-17).
 * Aquele par recortava o fundo no content-box, e como o recuo era só horizontal a área pintada do dia
 * escolhido saía 26×30 numa célula de 30×30 — um retângulo mais alto que largo. O designer comparou a
 * caixa da seleção com o anel do melhor dia do painel do aéreo (`AnelDoDia` em
 * `flight_planner_v2/painel_de_datas.tsx`, `inset-0` → 30×30 quadrado) e pediu a mesma forma nas duas:
 * mesmo raio (`--cell-radius`), mesma medida, quadrada.
 *
 * `bg-clip-border` e não a simples remoção do `bg-clip-content`: o `Button` traz `bg-clip-padding` no
 * CVA, e ele só coincide com a célula cheia enquanto o `border-0` daqui embaixo estiver de pé. Dizer
 * `border` explicitamente é o que faz a geometria não depender de outra classe.
 *
 * O recuo existia pra abrir vão entre camadas de COR de dias vizinhos — a rampa de preço do aéreo, que
 * é `inset-x-[2px]` na própria camada dela — e não pra encolher a seleção. E não há conflito: a
 * seleção APAGA o calor da célula (regra do `celulaDeCalor`), então a borda da seleção cheia encosta
 * nos 2px que a cor do VIZINHO não usa, sem vazar sobre ela.
 *
 * O hover não muda de tamanho junto porque ele deixou de ser fundo há muito tempo: é `ring-inset`
 * (item ① abaixo), desenhado no border-box, portanto já eram 30×30 — a nova geometria da seleção
 * alinhou a caixa pintada ao anel de hover em vez de desalinhar.
 *
 * A barra contínua do intervalo não quebra porque ela é pintada na CÉLULA (`range_*`), não no botão:
 * antes o botão desenhava as pontas ink encaixadas DENTRO da barra; agora elas coincidem com ela. O
 * `range_middle` segue sem recuo e sem raio, então a faixa continua contínua e sem fresta.
 *
 * **O intervalo inteiro é ink — pontas e meio.** Passou por creme (sand-100) e depois por ink a 12%
 * no meio, pra as duas extremidades dizerem qual é a ida e qual é a volta. Os dois se misturavam com
 * o verde e o amarelo claros da rampa de preço ("literalmente se misturando", em tela). A saída não
 * foi escurecer o meio mais um degrau: foi o designer mover a informação de lugar — **a confirmação
 * das datas passou pra dentro do botão** (`Confirmar 29 jul – 2 ago`). Com o texto dizendo quais são
 * as duas datas, a grade não precisa mais distinguir ponta de meio, e o intervalo pode ser um bloco
 * sólido — que é a leitura mais limpa possível contra um fundo que já tem cinco cores.
 *
 * **O recuo ASSIMÉTRICO das pontas saiu junto**, e o pedido que ele atendia continua atendido de graça.
 * Ele existia pra dois dias vizinhos virarem um bloco só — o designer: *"quando seleciono dois dias, eu
 * não preciso aplicar nada entre eles, eu posso juntar os dois em preto"*: com recuo simétrico, escolher
 * 18 e 19 desenhava dois quadrados ink separados por 4px de nada, e zerar a borda INTERNA de cada ponta
 * colava as duas. Sem recuo nenhum elas já nascem coladas, e o `forma` volta a decidir só o RAIO — que
 * é o que ele nunca conseguiu delegar (ver o item ③).
 *
 * **E as pontas só arredondam pra FORA.** O registry fazia
 * `data-[range-start=true]:rounded-(--cell-radius)` (os quatro cantos) e depois
 * `rounded-l-(--cell-radius)` de novo — ou seja, cada ponta ficava redonda dos dois lados. Com dois
 * dias em sequência isso desenhava duas pílulas encostadas em vez de um bloco: *"caso sejam dois dias
 * em sequência apenas, você fecha em preto como se fosse um retângulo só com round corner nas
 * extremidades, e não dois round corners"*. Agora `range_start` zera o canto direito e `range_end` o
 * esquerdo, então 19+20 lê como um único retângulo ink.
 *
 * **Um dia só volta a ter os quatro cantos redondos**, e isso cobre dois casos: o `selected_single`
 * (que nunca entrou nas regras de range) e — o que importa mais — o instante logo depois do PRIMEIRO
 * clique, quando o react-day-picker marca a mesma célula como `range_start` E `range_end`. Sem a
 * regra combinada as duas disparariam juntas e o dia ficaria quadrado de um lado sem ter vizinho
 * nenhum pra encostar. A variante empilhada `data-[range-start]:data-[range-end]` devolve o
 * arredondamento inteiro.
 *
 * E ela devolve por **`rounded-l-` + `rounded-r-`**, não pelo atalho `rounded-`: o atalho gera a
 * propriedade `border-radius`, enquanto os resets de lado geram as longhands
 * `border-top-right-radius`/`border-bottom-right-radius`. Misturar shorthand e longhand deixa o
 * resultado dependendo da ordem em que o Tailwind emitiu as regras, e na prática sobrava um canto
 * pontiagudo — o designer viu: *"quando eu tô com um dia só selecionado, ele tá mantendo algumas
 * arestas pontiagudas"*. Longhand contra longhand, quem tem mais especificidade ganha sempre. */
/* VOLL: três desvios do registry no botão do dia.
 *
 * ## ① O hover é TRAÇO, não preenchimento
 *
 * O ghost do Button pinta `bg-muted` no hover, e aqui isso é erro de conteúdo, não de estética: o
 * fundo da célula CARREGA informação (a faixa de preço do mapa de calor), então hover que pinta fundo
 * apaga o dado no instante em que a pessoa está comparando preços. Traço não substitui nada, só
 * adiciona. Daí o `hover:bg-transparent!` — o `!` porque a classe do ghost vem do CVA.
 *
 * 1.5px a 70%, e os dois números são relativos ao anel de HOJE (2px, ink cheio): o hover é
 * transitório e não pode ter o peso de um marcador permanente, mas em 1px a 45% sumia. `ring-inset`
 * porque, crescendo pra fora, invadiria o vão de 4px entre as células.
 *
 * As seis reafirmações de `bg`/`text` existem por causa desse `!`: ele apagava também o ink das
 * células ESCOLHIDAS, e passar o mouse no dia recém-escolhido deixava um quadrado cinza com número
 * claro. As regras de duas variantes têm mais especificidade e devolvem o ink onde ele existe.
 *
 * ## ② O anel de `data-focused` saiu
 *
 * O react-day-picker liga esse atributo no CLIQUE, não só na navegação por teclado, então todo dia
 * escolhido ganhava um halo cinza de 3px em volta do ink. Não se perde foco visível: o `Button` já
 * traz `focus-visible:ring-3`, que só dispara em teclado.
 *
 * Pelo mesmo motivo o foco vai com `preventScroll` (#3605): no toque, o iOS não foca o botão
 * sozinho, e o `focus()` programático rolava o dia pra vista. Na folha do celular o rolo tem
 * `snap-y mandatory`, e a animação pousava no início do mês mais próximo — a pessoa achava que
 * tinha marcado a data errada. O foco fica (o leitor de tela lê o dia); só o scroll sai — exceto
 * no teclado, em que um dia fora da vista é revelado sem animação.
 *
 * ## ③ O raio sai de JS, não de variantes de classe
 *
 * Esta é a parte que custou caro. A forma do dia depende da posição dele no intervalo — ponta
 * esquerda arredonda só pra fora, ponta direita idem, meio é reto, dia sozinho arredonda os quatro —
 * e a tentativa natural é escrever isso em `data-[range-*]:rounded-…`. Três rodadas depois, ainda
 * saía errado em algum caso: escolhendo de trás pra frente (clicar 9 e depois 3), o dia 9 virava
 * ponta final e continuava redondo dos dois lados.
 *
 * A causa é que essas variantes disputam entre si e com o `rounded-lg` do CVA misturando **shorthand
 * e longhand** (`border-radius` contra `border-top-left-radius`), e quem ganha passa a depender da
 * ordem em que o Tailwind emitiu cada regra — que não é a ordem em que a gente escreve. Medido no
 * navegador: nenhuma das regras de canto chegou a casar com o elemento.
 *
 * `modifiers` já diz exatamente onde a célula está no intervalo. Calcular a classe a partir dele
 * devolve UMA classe por canto em vez de seis competindo.
 *
 * **Os quatro casos declaram os DOIS lados, e com `!`.** Sem isso o `rounded-lg` do CVA sobrevive: o
 * tailwind-merge só remove uma classe anterior quando a posterior é do MESMO grupo, e `rounded-l-*`
 * não é do grupo de `rounded-*`. Então `rounded-lg` continuava na lista e ganhava o canto que a
 * branch tinha deixado em aberto — foi exatamente o que fez a ponta direita continuar redonda dos
 * dois lados. Declarar os dois lados em toda branch não deixa canto órfão, e o `!` garante que a
 * regra do CVA não volte a decidir por omissão.
 */
function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    const button = ref.current;
    if (!modifiers.focused || !button) return;
    button.focus({ preventScroll: true });
    // Teclado é a exceção: as setas levam o foco a um dia que pode estar fora
    // da vista na folha, e aí ele precisa aparecer. `:focus-visible` só liga
    // em foco vindo do teclado; no toque fica falso e nada rola. Instantâneo
    // (`auto`) e `nearest`: sem animação não há pouso do snap em outro mês.
    if (button.matches(':focus-visible')) {
      button.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [modifiers.focused]);

  /* A forma da célula pela posição dela no intervalo. Quatro casos e nada de cascata:
     meio          reto dos dois lados
     ponta esq.    arredonda à esquerda, reto à direita
     ponta dir.    reto à esquerda, arredonda à direita
     resto         os quatro cantos — inclui o dia sozinho, que o rdp marca como ponta esquerda E
                   direita ao mesmo tempo, e o dia não selecionado
     Só RAIO: o recuo lateral saiu com o `bg-clip-border` (2026-08-17), porque a caixa pintada passou
     a ser a célula cheia nos quatro casos. */
  const soPontaEsquerda = modifiers.range_start && !modifiers.range_end;
  const soPontaDireita = modifiers.range_end && !modifiers.range_start;
  let forma: string;
  if (modifiers.range_middle) {
    forma = 'rounded-l-none! rounded-r-none!';
  } else if (soPontaEsquerda) {
    forma = 'rounded-l-(--cell-radius)! rounded-r-none!';
  } else if (soPontaDireita) {
    forma = 'rounded-l-none! rounded-r-(--cell-radius)!';
  } else {
    forma = 'rounded-l-(--cell-radius)! rounded-r-(--cell-radius)!';
  }

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        forma,
        'bg-clip-border hover:bg-transparent! hover:ring-[1.5px] hover:ring-foreground/70 hover:ring-inset hover:data-[selected-single=true]:bg-primary! hover:data-[range-start=true]:bg-primary! hover:data-[range-end=true]:bg-primary! hover:data-[range-middle=true]:bg-primary! hover:data-[range-middle=true]:text-primary-foreground! hover:data-[selected-single=true]:text-primary-foreground! hover:data-[range-start=true]:text-primary-foreground! hover:data-[range-end=true]:text-primary-foreground! data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:bg-primary data-[range-middle=true]:text-primary-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 border-0 leading-none font-normal data-[range-start=true]:[&>span]:text-xs [&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

/**
 * O próximo `selected`, a partir do que o `mode="range"` acabou de devolver — dois desvios
 * conscientes do comportamento cru do react-day-picker, ambos vindos de calendário de VIAGEM
 * (hotel e, com `minNoites: 0`, também aéreo):
 *
 * ① **Reinício no estilo Google Flights.** Com um período já FECHADO (`anterior` tem `from` e `to`
 *    em dias diferentes — mesmo critério de `temVolta` em
 *    `flight_planner_v2/painel_de_datas.tsx`), o próximo clique em QUALQUER dia não estica nem
 *    encolhe o intervalo: reinicia a seleção nesse dia. É assim que Google Flights lê o clique —
 *    intervalo fechado é estado terminal, não editável célula a célula — e evita o efeito colateral
 *    do algoritmo padrão do rdp (`addToRange`), que decidiria virar `from` ou `to` conforme o dia
 *    clicado cair antes ou depois do intervalo existente.
 *
 * ② **O dia duplo do primeiro clique, só quando 1 noite é o mínimo.** O primeiro clique em
 *    `mode="range"` sempre devolve `{ from: dia, to: dia }` (é o `addToRange` do próprio rdp). Pra
 *    hotel — onde `minNoites` é 1, ninguém reserva 0 diária — isso é um estado que não pode existir
 *    como seleção: a UI trataria `to` presente como estadia confirmável (0 noites) e a CÉLULA
 *    ganharia `range_start` E `range_end` no mesmo dia, os dois `::after` da barra colidindo (um com
 *    `right-0`, outro com `left-0` — o arredondamento dos dois lados que a nota grande de
 *    `CalendarDayButton` documenta). Descartar o `to` devolve o dia a "só ida escolhida", que é o
 *    estado real: só houve UM clique. Aéreo passa `minNoites: 0` porque lá ida-e-volta no mesmo dia
 *    É uma estadia válida (dia inteiro no destino) — quem decide se aquilo é intervalo fechado é
 *    `temVolta`, não este helper.
 *
 * Fora desses dois casos, `selecao` sai como veio: o rdp já resolveu a mecânica normal de estender
 * um intervalo aberto.
 */
export function proximaSelecao(
  anterior: DateRange | undefined,
  selecao: DateRange | undefined,
  diaClicado: Date,
  { minNoites }: { minNoites: 0 | 1 },
): DateRange | undefined {
  const anteriorFechado =
    anterior?.from != null &&
    anterior.to != null &&
    anterior.to.toDateString() !== anterior.from.toDateString();
  if (anteriorFechado) return { from: diaClicado, to: undefined };

  const primeiroCliqueDuplicado =
    minNoites === 1 &&
    selecao?.from != null &&
    selecao.to?.toDateString() === selecao.from.toDateString();
  if (primeiroCliqueDuplicado) return { from: selecao.from, to: undefined };

  return selecao;
}

export { Calendar, CalendarDayButton };
